import DateRangeIcon from '@mui/icons-material/DateRange';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MonetizationOnOutlined from '@mui/icons-material/MonetizationOnOutlined';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import {
  Chip,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import Box from '@mui/material/Box';
import {blue, green, orange, red} from '@mui/material/colors';
import React, { JSX, useState } from 'react';
import {GrantEventType, GrantLogEntry} from '../../../../model/license/grant-log-entry.model';
import {LicenseDetails} from '../../../../model/license/license-details.model';
import {OrganizationData} from '../../../../model/organization/organization-data';
import {FormatUtils} from '../../../../utils/format.utils';

type Props = {
  organizationData: OrganizationData
  licenseDetails: LicenseDetails,
  grantLog: GrantLogEntry[]
  translate: (key: string) => string
  translateLocal: (key: string) => string
  navigateToUsers: () => void
}

type HeaderItem = {
  icon: JSX.Element
  value: string
  title: string
}

const EVENT_COLOR: Record<GrantEventType, string> = {
  GRANTED: green[700],
  EXTENDED: blue[700],
  EXPIRED: red[600],
};

export const OrganizationLicenseDetailsHeader = ({licenseDetails, navigateToUsers, organizationData, grantLog, translate, translateLocal}: Props) => {
  const [expanded, setExpanded] = useState(false);

  const headerItems: HeaderItem[] = [
    { icon:
        <IconButton onClick={() => navigateToUsers()} aria-label={translate('common.details.title')}>
          <PeopleOutlinedIcon />
        </IconButton>,
      value: organizationData.name, title: translateLocal('header.organization.title')},
    { icon: <DescriptionOutlinedIcon />, value: translate(`licenseType.${licenseDetails.licenseType}`), title: translateLocal('header.license-type.title')},
    { icon: <MonetizationOnOutlined />, value: FormatUtils.formatCurrency(licenseDetails.monthlyCost), title: translateLocal('header.base-cost.title')},
    { icon: <WhatshotIcon />, value: `${licenseDetails.discount.toFixed(2)}%`, title: translateLocal('header.discount.title')},
    { icon: <MonetizationOnOutlined />, value: FormatUtils.formatCurrency(licenseDetails.customerMonthlyCost), title: translateLocal('header.customer-cost.title')},
    { icon: <DateRangeIcon />, value: FormatUtils.formatDate(licenseDetails.licenseExpiredDate), title: translateLocal('header.expired-date.title')},
  ]

  const buildSimpleHeaderItem = (item: HeaderItem, index: number) => {
    return <Box key={index} display="flex" alignItems="center" gap={1}>
      {item.icon}
      <Typography variant="body2" fontWeight="bold">
        {item.title}:
      </Typography>
      <Typography variant="body2">{item.value}</Typography>
    </Box>
  }

  // Historia nadan (log): jeden wiersz = jedno zdarzenie (kupil / przedluzyl / wygaslo).
  const buildGrantLogRow = (entry: GrantLogEntry, index: number) => (
    <Stack key={`${entry.id}-${index}`} direction="row" spacing={2} alignItems="center" ml={4}>
      <Chip
        size="small"
        label={translateLocal(`grant-log.event.${entry.eventType}`)}
        sx={{ bgcolor: EVENT_COLOR[entry.eventType], color: 'white', fontWeight: 600, minWidth: 92 }}
      />
      <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 220 }}>{translate(`licenseDetails.feature.type.${entry.featureType}`)}</Typography>
      <Chip size="small" variant="outlined" label={translate(`subscriptionType.${entry.subscriptionType}`)} />
      <Box display="flex" alignItems="center" gap={0.5}>
        <MonetizationOnOutlined fontSize="small" sx={{ color: orange[800] }} />
        <Typography variant="body2">{FormatUtils.formatCurrency(entry.monthlyCost)}</Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={0.5}>
        <DateRangeIcon fontSize="small" />
        <Typography variant="body2">{entry.validUntil ? FormatUtils.formatDate(entry.validUntil) : '-'}</Typography>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
        {FormatUtils.formatDate(entry.createdTimestamp)}
      </Typography>
    </Stack>
  );

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={4} alignItems="center">
        {grantLog.length > 0 && (
          <IconButton onClick={() => setExpanded((prev) => !prev)} size="small" title={translateLocal('grant-log.title')}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
        {headerItems.map((item, index) => buildSimpleHeaderItem(item, index))}
      </Stack>

      {expanded && (
        <Stack spacing={1}>
          <Box display="flex" alignItems="center" gap={1} ml={1}>
            <HistoryIcon fontSize="small" />
            <Typography variant="subtitle2">{translateLocal('grant-log.title')}</Typography>
          </Box>
          {grantLog.map((entry, index) => buildGrantLogRow(entry, index))}
        </Stack>
      )}
    </Stack>
  );
}
